import React from "react";

/**
 * ClydeFlow SVG wordmark — crisp at any size, no image artifacts.
 * - "Clyde" in white
 * - "Flow" in cyan→teal gradient with light italic feel
 * - Optional tagline "INTELLIGENT AUTOMATION"
 */
export const Wordmark = ({
  className = "h-7",
  showTagline = false,
  ...rest
}) => {
  // Single shared gradient id, but unique per render to avoid SVG defs collision
  const gid = React.useId();
  return (
    <svg
      viewBox={showTagline ? "0 0 320 90" : "0 0 320 64"}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="ClydeFlow"
      className={className}
      data-testid="brand-wordmark"
      {...rest}
    >
      <defs>
        <linearGradient id={`cf-grad-${gid}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#00F0FF" />
          <stop offset="55%" stopColor="#38BDF8" />
          <stop offset="100%" stopColor="#10B981" />
        </linearGradient>
      </defs>

      {/* Icon: dark rounded square with cyan flow dashes */}
      <g transform="translate(0,8)">
        <rect
          x="0"
          y="0"
          width="48"
          height="48"
          rx="10"
          fill="#0A192F"
          stroke="rgba(0,240,255,0.25)"
          strokeWidth="1"
        />
        <g stroke={`url(#cf-grad-${gid})`} strokeWidth="3" strokeLinecap="round" fill="none">
          <path d="M10 18 H22" />
          <path d="M14 26 H30" />
          <path d="M20 34 H38" opacity="0.9" />
        </g>
      </g>

      {/* Wordmark text */}
      <g
        fontFamily='"Bricolage Grotesque", "Manrope", sans-serif'
        fontWeight="800"
        letterSpacing="-1.2"
      >
        <text x="60" y="42" fontSize="38" fill="#FFFFFF">
          Clyde
        </text>
        <text
          x="155"
          y="42"
          fontSize="38"
          fill={`url(#cf-grad-${gid})`}
          fontStyle="italic"
          fontWeight="600"
        >
          Flow
        </text>
      </g>

      {showTagline && (
        <text
          x="60"
          y="74"
          fontFamily='"JetBrains Mono", monospace'
          fontSize="9"
          fill="#94A3B8"
          letterSpacing="3"
        >
          INTELLIGENT AUTOMATION
        </text>
      )}
    </svg>
  );
};

/** Just the icon mark, square. */
export const Icon = ({ className = "h-9 w-9", ...rest }) => {
  const gid = React.useId();
  return (
    <svg
      viewBox="0 0 48 48"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="ClydeFlow icon"
      data-testid="brand-icon"
      {...rest}
    >
      <defs>
        <linearGradient id={`cf-icon-grad-${gid}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#00F0FF" />
          <stop offset="55%" stopColor="#38BDF8" />
          <stop offset="100%" stopColor="#10B981" />
        </linearGradient>
      </defs>
      <rect
        x="0"
        y="0"
        width="48"
        height="48"
        rx="10"
        fill="#0A192F"
        stroke="rgba(0,240,255,0.25)"
        strokeWidth="1"
      />
      <g stroke={`url(#cf-icon-grad-${gid})`} strokeWidth="3" strokeLinecap="round" fill="none">
        <path d="M10 18 H22" />
        <path d="M14 26 H30" />
        <path d="M20 34 H38" opacity="0.9" />
      </g>
    </svg>
  );
};
